import { useMemo } from "react";
import type { PresetRef } from "../contract/types";
import { normalizeAssessmentCopy } from "../lib/assessment";
import { Icon } from "../components/Icon";
import type { BlockEventSink } from "../blocks/types";
import { asRecord, type PresetData } from "./data";
import { CourseOverview } from "./CourseOverview";
import { UnitDashboard } from "./UnitDashboard";
import { ConceptDeepDive } from "./ConceptDeepDive";
import { CaseStudy } from "./CaseStudy";
import { AssessmentBrief } from "./AssessmentBrief";
import { StudyPath } from "./StudyPath";

/**
 * Despachador de presets: `name` elige la vista, `data` la rellena.
 *
 * Un preset desconocido no se pinta en blanco: se dice qué llegó y se sigue.
 * Es la misma política que aplica `Unknown` con los átomos.
 */

export interface PresetRendererProps {
  preset: PresetRef;
  emit: BlockEventSink;
  interactive: boolean;
}

export function PresetRenderer({ preset: sourcePreset, emit, interactive }: PresetRendererProps) {
  const preset = useMemo(() => normalizeAssessmentCopy(sourcePreset), [sourcePreset]);
  const data: PresetData = useMemo(() => asRecord(preset.data) ?? {}, [preset.data]);
  const view = { preset, data, emit, interactive };

  switch (preset.name) {
    case "course_overview":
      return <CourseOverview {...view} />;
    case "unit_dashboard":
      return <UnitDashboard {...view} />;
    case "concept_deep_dive":
      return <ConceptDeepDive {...view} />;
    case "case_study":
      return <CaseStudy {...view} />;
    case "assessment_brief":
      return <AssessmentBrief {...view} />;
    case "study_path":
      return <StudyPath {...view} />;
    default:
      return (
        <div className="preset__unknown">
          <Icon name="alert" size="1.25rem" />
          <p>
            La vista <code className="u-mono">{preset.name}</code> no está implementada en esta
            versión de la interfaz.
          </p>
          <pre className="u-mono">{JSON.stringify(preset.data, null, 2)}</pre>
        </div>
      );
  }
}

export default PresetRenderer;
